<template>
  <template v-if="type === 'pop-up' && !isMobile">
    <overlay
      ref="overlay"
      key="quick-add-success-modal"
      block-class="quick-add-success-modal"
      namespace="quick-add-success-modal"
      direction="up"
      type="modal"
      :show-overlay="false"
      :show-close="false"
      :disable-scroll="false"
    >
      <template #body>
        <div
          v-click-outside="handleClickOutside"
          class="quick-add-success"
          :class="`${type} ${successClass}`"
        >
          <icon-check />

          <button
            class="overlay__close icon-button"
            :class="[
              $blockClass(blockClass, 'close'),
            ]"
            type="button"
            @click.prevent="closePopup"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.close')"
            />

            <slot name="close">
              <icon-close />
            </slot>
          </button>

          <p
            class="
              quick-add-success__title
              text-body-2xs-regular
            "
            v-text="successMessage"
          />

          <responsive-image
            class="quick-add-success__image"
            :image="getProductImage(stateProduct)"
            :padding="false"
            ratio="1:1"
            sizes="25vw"
            widths="350"
          />

          <div
            :class="`quick-add-success__buttons-wrapper ${
              enforceHideBagOnDesktop && !isMobile ? 'has-hidden-cart-btn' : ''
            }`"
          >
            <btn
              v-if="viewBag && !(enforceHideBagOnDesktop && !isMobile)"
              class="quick-add-success__view-cart-button"
              :label="$string('product.cta.view_cart')"
              modifiers="primary block center"
              type="button"
              :url="$variable('routes.cart.url')"
            />

            <btn
              v-if="continueButton"
              class="quick-add-success__continue-button"
              :label="$string('product.cta.continue_shopping')"
              modifiers="outline block center"
              type="button"
              @click="close"
            />

            <product-matching-set
              v-if="showDesktopUpsell"
              :product="stateProduct"
              :placement="placement"
              @shows-upsell="setContinueButtonVisibility"
            />
          </div>
        </div>
      </template>
    </overlay>
  </template>

  <template v-else>
    <div
      class="quick-add-success"
      :class="type"
    >
      <icon-check />

      <p
        class="
          quick-add-success__title
          text-body-2xs-regular
        "
        v-text="successMessage"
      />

      <responsive-image
        class="quick-add-success__image"
        :image="getProductImage(product || stateProduct)"
        :padding="false"
        ratio="1:1"
        sizes="25vw"
        widths="350"
      />

      <div
        :class="`quick-add-success__buttons-wrapper ${
          enforceHideBagOnDesktop && !isMobile ? 'has-hidden-cart-btn' : ''
        }`"
      >
        <product-matching-set
          v-if="showUpsell"
          :product="stateProduct"
          :placement="placement"
          @shows-upsell="setContinueButtonVisibility"
        />

        <btn
          v-if="viewBag && !(enforceHideBagOnDesktop && !isMobile)"
          class="quick-add-success__view-cart-button"
          :label="$string('product.cta.view_cart')"
          modifiers="primary block center"
          type="button"
          :url="$variable('routes.cart.url')"
        />

        <btn
          v-if="continueButton &&
            (!stateProduct?.metafields?.matchingSet?.product ||
              isShowingUpsell)
          "
          class="quick-add-success__continue-button"
          :label="$string('product.cta.continue_shopping')"
          modifiers="outline block center"
          type="button"
          @click="closeInline"
        />
      </div>
    </div>
  </template>
</template>

<script>

/**
 * Vue: Quick add success (quick-add-success)
 * -----------------------------------------------------------------------------
 * Quick add success message - pop-up and inline.
 *
 * @param {Boolean} [continueButton] - Show continue button
 * @param {Array} [product] - Product added to cart.
 * @param {String} [size] - Added product size
 * @param {String} [type] - Success message type ['pop-up', 'inline']
 * @param {Boolean} [viewBag] - Show view bag button
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ResponsiveImage from '~global/images/responsive-image'

import ProductMatchingSet from '~async/product-matching-set/product-matching-set'

import IconCheck from '~icons/general/check.svg'
import IconClose from '~icons/directional-navigation/close-outline.svg'

export default {
  name: 'QuickAddSuccess',

  components: {
    Btn,
    IconCheck,
    IconClose,
    Overlay,
    ProductMatchingSet,
    ResponsiveImage,
  },

  props: {
    continueButton: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    size: {
      type: [Boolean, String],
      default: false,
    },
    type: {
      type: String,
      default: 'inline',
    },
    viewBag: {
      type: Boolean,
      default: true,
    },
    showUpsell: {
      type: Boolean,
      default: true,
    },
    placement: {
      type: String,
      default: 'quickAddSuccess',
    },
  },

  emits: {
    'success-message-close': () => true,
  },

  data() {
    return {
      delay: 5000,
      start: false,
      timeout: false,
      successClass: '',
      isShowingUpsell: true,
      blockClass: 'quick-add-success',
      enforceHideBagOnDesktop: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      dyTests: (state) => state['dynamic-yield'].tests,
      isMobile: (state) => state.index.screen.mobile,
      stateProduct: (state) => state.quickAddToCart.product,
      stateSize: (state) => state.quickAddToCart.size,
      showDesktopUpsell: (state) => state.productFormDrawer.showOnSuccess,
    }),

    /**
     * Compute success message.
     * @returns {String}
     */
    successMessage() {
      const size = this.size || this.stateSize

      if (!size || ['os', 'one size'].includes(size?.toLowerCase())) {
        return this.$string('product.cta.added_to_cart')
      }

      return this.$string('product.cta.success_added_size', { replace: { size } })
    },

  },

  watch: {

    /**
     * Watch DY tests to activate them.
     */
    dyTests: {
      handler() {
        if (this.dyTests.includes('dy-atc-success-hide-view-cart-btn')) {
          this.enforceHideBagOnDesktop = true
        }
      },
      deep: true,
      immediate: true,
    },

    /**
     * Watch active overlay to auto-close pop-up after delay.
     */
    activeOverlay() {
      if (this.activeOverlay?.namespace !== 'quick-add-success-modal') {
        return
      }

      this.successInlinePaddingClass()
    },

    isShowingUpsell() {
      this.successInlinePaddingClass()
    },
  },

  methods: {

    /**
     * Close pop-up after delay.
     */
    ...mapActions({
      setProduct: 'quickAddToCart/setQuickAddProduct',
    }),

    /**
     * Close pop up with delay.
     */
    closePopupDelayed() {
      const delay = this.delay - (performance.now() - this.start)

      this.timeout = setTimeout(() => {
        this.closePopup()
      }, delay)
    },

    /**
     * Close pop-up immediately.
     */
    closePopup() {
      this.$refs.overlay?.close('button')
      this.setProduct(false)

      setTimeout(() => {
        this.successClass = ''
        this.isShowingUpsell = true
      }, this.$timing('normal'))
    },

    /**
     * Pause pop-up close.
     */
    pauseClose() {
      clearTimeout(this.timeout)
    },

    /**
     * Close inline overlay.
     */
    closeInline() {
      this.$emit('success-message-close')
      this.setProduct(false)
    },

    /**
     * Handle click outside.
     */
    handleClickOutside() {
      if (this.activeOverlay?.namespace !== 'quick-add-success-modal') {
        return
      }

      this.setProduct(false)
      this.pauseClose()
      this.$refs.overlay?.close('overlay')
    },

    /**
     * Check first if product has variant image
     * @param product current product
     */
    getProductImage(product) {
      return product.selectedOrFirstAvailableVariant?.image?.url
        ? product.selectedOrFirstAvailableVariant?.image
        : product.featuredImage
    },

    successInlinePaddingClass() {
      this.successClass = !this.isShowingUpsell && this.stateProduct && !this.isMobile ? 'inline-zero-padding' : ''
    },

    /**
     * Sets upsell product will show
     * to coordinate continue button on success modal
     */
    setContinueButtonVisibility(value) {
      this.isShowingUpsell = value
    },
  },
}
</script>

<style lang="scss">
@import './quick-add-success';
</style>
